import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import type { Account } from "@ca/shared";
import { Comparison, Plan, ProStatus, Upsell } from "@ca/shared";
import { Button, Card, Chip, ErrorState, Modal, PageHead, Skeleton } from "@ca/ui";
import { api, HttpError } from "./api.ts";
import { uuidv7 } from "./queue.ts";
export function usePro(accountId: string) {
  return useQuery({
    queryKey: ["pro", accountId],
    queryFn: async () => ProStatus.parse(await api("/v1/me/pro")),
    refetchInterval: (query) => {
      const expires = query.state.data?.expiresAt;
      return expires && new Date(expires).getTime() > Date.now()
        ? Math.min(15000, Math.max(500, new Date(expires).getTime() - Date.now()))
        : 15000;
    },
    staleTime: 0,
  });
}
export function UpsellDialog({ accountId }: { accountId: string }) {
  const [upsell, setUpsell] = useState<z.infer<typeof Upsell> | null>(null);
  const navigate = useNavigate();
  useEffect(() => {
    setUpsell(null);
    const show = (event: Event) => {
      const parsed = Upsell.safeParse((event as CustomEvent<unknown>).detail);
      if (parsed.success) setUpsell(parsed.data);
    };
    window.addEventListener("ca-upsell", show);
    return () => {
      window.removeEventListener("ca-upsell", show);
    };
  }, [accountId]);
  if (!upsell) return null;
  return (
    <Modal
      title={upsell.title}
      onClose={() => {
        setUpsell(null);
      }}
    >
      <p className="muted">{upsell.message}</p>
      {Object.entries(upsell.counts).map(([key, value]) => (
        <p key={key} className="list-row">
          {key === "notes"
            ? "Ghi chú của bạn"
            : key === "lockedItems"
              ? "Nội dung mở thêm"
              : key === "wrongItems"
                ? "Câu sai chưa thành thạo của bạn"
                : key}
          : {value}
        </p>
      ))}
      <Button
        variant="acc"
        style={{ marginTop: 12 }}
        onClick={() => {
          setUpsell(null);
          void navigate({ to: "/$", params: { _splat: "pro" } });
        }}
      >
        Xem các gói Pro →
      </Button>
    </Modal>
  );
}
const months = (p: z.infer<typeof Plan>) =>
  p.durationDays === null ? 36 : p.durationDays === 365 ? 12 : p.durationDays / 30;
const monthly = (p: z.infer<typeof Plan>) => Math.round(p.priceVnd / months(p) / 1000) * 1000;
const money = (n: number) => `${n.toLocaleString("vi-VN")} đ`;
export function ProPage({ accountId, notify }: { accountId: string; notify: (s: string) => void }) {
  const client = useQueryClient();
  const plans = useQuery({
    queryKey: ["plans"],
    queryFn: async () => z.array(Plan).parse(await api("/v1/plans")),
  });
  const compare = useQuery({
    queryKey: ["pro-compare"],
    queryFn: async () => Comparison.parse(await api("/v1/pro/compare")),
  });
  const status = usePro(accountId);
  const [selected, setSelected] = useState("q");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Keep key + payload unchanged after an ambiguous network error. This write
  // requires server confirmation and never grants Pro optimistically/offline.
  const pending = useRef<{ key: string; code: string } | null>(null);
  const codeInput = useRef<HTMLInputElement>(null);
  async function redeem() {
    if (busy || !code.trim()) return;
    const write = pending.current ?? { key: uuidv7(), code: code.trim() };
    pending.current = write;
    setBusy(true);
    setError("");
    try {
      const result = z.object({ pro: ProStatus }).parse(
        await api("/v1/me/redeem", {
          method: "POST",
          key: write.key,
          body: { code: write.code },
          accountId,
        }),
      );
      pending.current = null;
      setCode("");
      // An idempotent replay describes the original write; refresh current rights
      // so a later revocation/expiry cannot re-enable even the displayed badge.
      client.setQueryData(["pro", accountId], result.pro);
      client.setQueryData<Account>(["me"], (current) =>
        current?.id === accountId ? { ...current, pro: result.pro } : current,
      );
      await status.refetch();
      notify("Đã kích hoạt Pro. Tiến độ của bạn được giữ nguyên.");
    } catch (e) {
      if (e instanceof HttpError && e.status >= 400 && e.status < 500) pending.current = null;
      setError(
        e instanceof Error
          ? e.message
          : "Chưa nhận được xác nhận. Bấm kích hoạt để gửi lại cùng thao tác.",
      );
    } finally {
      setBusy(false);
    }
  }
  if (plans.isPending || compare.isPending) return <Skeleton />;
  if (plans.isError || compare.isError)
    return (
      <ErrorState
        message={(plans.error ?? compare.error)?.message ?? "Không tải được gói Pro."}
        onRetry={() => {
          void plans.refetch();
          void compare.refetch();
        }}
      />
    );
  const plan = plans.data.find((p) => p.code === selected) ?? plans.data[0];
  const pro = status.data;
  const active = !!pro?.active && (pro.expiresAt === null || new Date(pro.expiresAt) > new Date());
  const base = plans.data.find((p) => p.durationDays === 30);
  const savings = (p: z.infer<typeof Plan>) =>
    base && base.priceVnd > 0 ? Math.max(0, Math.round((1 - monthly(p) / base.priceVnd) * 100)) : 0;
  return (
    <>
      <PageHead
        space="common"
        title="Mở khoá toàn bộ kho đề và lời giải"
        desc={`Bản miễn phí đủ để bạn thử. Bản Pro mở hết ${compare.data.totals.ets ?? 0} đề ETS thật, ${compare.data.totals.mock_published ?? 0}/${compare.data.totals.mock ?? 0} đề thi thử đã xuất bản, lời giải chi tiết từng câu kèm tên bẫy, và ${(compare.data.totals.words ?? 0).toLocaleString("vi-VN")} từ vựng theo ${compare.data.totals.topics ?? 0} chủ đề TOEIC.`}
      />
      {/* Gói hiện tại: chỉ staging có (S12), thu gọn thành một dải trên hai thẻ giá. */}
      <div className={active ? "resume" : "card tight row between"} role="status">
        {status.isPending ? (
          <span className="faint">Đang kiểm tra gói hiện tại…</span>
        ) : status.isError ? (
          <ErrorState message={status.error.message} onRetry={() => void status.refetch()} />
        ) : (
          <div className="row between" style={{ width: "100%" }}>
            <span>
              <b>Gói hiện tại: {active ? "👑 Pro" : "☆ Miễn phí"}</b>
              {active && (
                <span className="muted">
                  {" "}
                  ·{" "}
                  {pro.expiresAt
                    ? `hết hạn ${new Date(pro.expiresAt).toLocaleString("vi-VN")}`
                    : "trọn đời"}
                </span>
              )}
            </span>
            <span className="faint" style={{ fontSize: 12.5, fontWeight: 600 }}>
              Gia hạn cộng thêm thời gian vào gói còn hiệu lực · không tự động gia hạn
            </span>
          </div>
        )}
      </div>
      <div className="grid g2">
        <section className="card stack">
          <h2>☆ Miễn phí · 0 đ</h2>
          <p className="muted">Dùng mãi mãi · không cần thẻ</p>
          {compare.data.rows.map((r) => (
            <div key={r.label} className="list-row">
              <span>{r.free === r.pro ? "✓" : "🔒"}</span>
              <div className="grow">
                <b>{r.label}</b>
                <div className="faint" style={{ fontSize: 12.5 }}>
                  {r.free}
                </div>
              </div>
            </div>
          ))}
        </section>
        <section className="card stack" style={{ outline: "2px solid var(--accent-2)" }}>
          <div className="row between">
            <h2>👑 Pro</h2>
            <Chip tone="acc">{plan?.badge ?? "Phổ biến nhất"}</Chip>
          </div>
          <div className="seg" role="group" aria-label="Thời hạn" style={{ flexWrap: "wrap" }}>
            {plans.data.map((p) => (
              <button
                key={p.code}
                aria-pressed={plan?.code === p.code}
                onClick={() => {
                  setSelected(p.code);
                }}
              >
                {p.name}
                {savings(p) > 0 ? ` −${savings(p)}%` : ""}
              </button>
            ))}
          </div>
          {plan ? (
            <>
              <b style={{ fontSize: 38, letterSpacing: "-.02em" }}>{money(plan.priceVnd)}</b>
              <p className="muted" style={{ fontSize: 13 }}>
                ≈ {money(monthly(plan))}/tháng
                {plan.durationDays === null ? " (so sánh trên 36 tháng)" : ""} ·{" "}
                {plan.durationDays === null
                  ? "trả một lần, dùng mãi — kể cả các đề và tính năng ra sau này."
                  : `thanh toán một lần cho ${plan.name.toLowerCase()}, không tự gia hạn.`}
              </p>
              {plan.note && (
                <p className="faint" style={{ fontSize: 12.5 }}>
                  {plan.note}
                </p>
              )}
              {/* Ra mắt bằng mã kích hoạt, thanh toán làm sau: nút mua đưa tới ô nhập mã. */}
              <Button
                variant="acc"
                size="lg"
                onClick={() => {
                  codeInput.current?.focus();
                  notify("Nhập mã kích hoạt bạn nhận được khi mua gói.");
                }}
              >
                Mua gói {plan.name.toLowerCase()} · {money(plan.priceVnd)}
              </Button>
            </>
          ) : (
            <p className="faint">Chưa có gói đang bán.</p>
          )}
          {compare.data.rows
            .filter((r) => r.free !== r.pro)
            .map((r) => (
              <div key={r.label} className="list-row">
                <span>✓</span>
                <div className="grow">
                  <b>{r.label}</b>
                  <div className="faint" style={{ fontSize: 12.5 }}>
                    {r.pro}
                  </div>
                </div>
              </div>
            ))}
          <form
            className="stack"
            style={{ gap: 6 }}
            onSubmit={(e) => {
              e.preventDefault();
              void redeem();
            }}
          >
            <div className="row">
              <input
                id="activation-code"
                className="input grow"
                ref={codeInput}
                value={code}
                maxLength={80}
                disabled={busy || pending.current !== null}
                autoComplete="off"
                spellCheck={false}
                aria-label="Mã kích hoạt"
                placeholder="✨ Mã kích hoạt"
                onChange={(e) => {
                  setCode(e.target.value);
                }}
              />
              <Button disabled={busy || !code.trim()} type="submit">
                {busy ? "Đang kích hoạt…" : "Kích hoạt"}
              </Button>
            </div>
            {error && (
              <p
                role="alert"
                className="chip bad"
                style={{ height: "auto", padding: "6px 10px", whiteSpace: "normal" }}
              >
                {error}
              </p>
            )}
            {pending.current && !busy && (
              <Button
                variant="line"
                size="sm"
                style={{ alignSelf: "flex-start" }}
                onClick={() => {
                  pending.current = null;
                  setError("");
                  setCode("");
                }}
              >
                Nhập mã khác
              </Button>
            )}
          </form>
        </section>
      </div>
      <Card>
        <h3 style={{ marginBottom: 6 }}>Câu hỏi hay gặp</h3>
        {compare.data.faq.map((f) => (
          <div key={f.question} className="list-row" style={{ alignItems: "flex-start" }}>
            <div>
              <b>{f.question}</b>
              <p className="muted" style={{ fontSize: 13.5 }}>
                {f.answer}
              </p>
            </div>
          </div>
        ))}
      </Card>
    </>
  );
}
